/**
 * Example TanStack Query hooks for the Users module.
 *
 * These establish the convention for the data layer — pair `api` (the typed
 * client) with `queryKeys` (the central key factory) and type the result with
 * the shared types in `@/types/api`. Phase 1 should follow this pattern for
 * the remaining endpoints rather than calling `api` directly from components.
 */

import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import type { Paginated, User } from '@/types/api';

export interface UserListParams {
  page?: number;
  limit?: number;
  type?: 'latest' | 'top' | 'suggested' | 'nearest';
  q?: string;
  /** Field → value filters (e.g. `{ city: 'Mumbai' }` → `filters[city]=Mumbai`). */
  filters?: Record<string, string | undefined>;
  /**
   * When set to `'Y'` on a `type='latest'` query, the backend includes a
   * `live: boolean` field on each user (true for currently live-streaming)
   * and sorts live users first. Mirrors the legacy `user/list?live=Y` param.
   */
  live?: 'Y';
}

export interface UserListOptions {
  /** Skip the request entirely when `false` (e.g. <2-char search query). */
  enabled?: boolean;
  /**
   * Background refetch cadence (ms). Pass when the response carries
   * fast-changing data — e.g. `live=Y` queries that need to reflect a
   * broadcaster going on/offline without the user reloading.
   */
  refetchInterval?: number;
  /** Override the default 60s `staleTime` (e.g. shorter for live data). */
  staleTime?: number;
}

/** Serialise params (including bracketed `filters[…]`) into a query string. */
function toQuery(params: UserListParams = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (key === 'filters' && typeof value === 'object') {
      for (const [fk, fv] of Object.entries(value as Record<string, unknown>)) {
        if (fv === undefined || fv === null || fv === '') continue;
        search.set(`filters[${fk}]`, String(fv));
      }
      continue;
    }
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/** Paginated / searchable list of users (`GET /user/list`, public, 60s cache). */
export function useUserList(
  params: UserListParams = {},
  options: UserListOptions = {},
) {
  return useQuery({
    queryKey: queryKeys.users.list(params),
    queryFn: () => api.get<Paginated<User>>(`user/list${toQuery(params)}`),
    staleTime: options.staleTime ?? 60_000,
    refetchInterval: options.refetchInterval,
    enabled: options.enabled ?? true,
  });
}

/** Full profile for a single user (`GET /user/details/{id}`). */
export function useUserDetails(id: number | string | undefined) {
  return useQuery({
    queryKey: queryKeys.users.details(id ?? ''),
    queryFn: () => api.get<User>(`user/details/${id}`),
    enabled: id !== undefined && id !== null && id !== '',
    // Shares queryKey with `useProfile` (by-username path) — match
    // its 1 min retention so cache eviction is symmetric.
    gcTime: 60_000,
  });
}
